/**
 * The update trace report (docs/spec/01d-update-trace.md): the OLD page's boot
 * script records every step of an update offer in localStorage
 * (`window.__sendlyTrace` in index.html — the offer, the warm-up reply, each
 * banner tap and pull-to-refresh, the 3 s fallback, the waiting worker's
 * states, controllerchange, the reload). The NEW page takes that record once
 * and sends it as ONE crashLogs row — only when the update was slow, or when it
 * landed on the same build — together with its own navigation timing, which
 * says how long the reload's HTML request waited (a worker held in
 * `activating` queues it).
 *
 * Same shape as chunkRecoveryReport.js: the old page cannot report (a write
 * right before a reload can be lost, and the slowness is only known here).
 */
import { useEffect } from "react";
import { buildId as BUILD_ID } from "../buildInfo";
import { REPORT_DELAY_MS } from "../hooks/useClientCompat";
import { logCrashError } from "./crashLogger";
import { localStore } from "./safeStorage";

/** The record the boot script writes; kept in step with TRACE_KEY in index.html. */
export const UPDATE_TRACE_KEY = "sendly-update-trace";

/** A record older than this is not this update's (TRACE_MAX_AGE_MS in index.html). */
export const UPDATE_TRACE_MAX_AGE_MS = 15 * 60 * 1000;

/** From the first tap / pull to this document's HTML: slower than this is reported. */
export const UPDATE_TRACE_SLOW_MS = 5000;

/**
 * Projects where EVERY update with a tap / pull is reported, fast ones too —
 * the test project, where updates are tried on purpose and a fast run is as
 * much a data point as a slow one. Production reports only slow updates.
 */
export const ALWAYS_REPORT_PROJECTS = Object.freeze(["test-sendly"]);

function alwaysReportHere() {
  try {
    return ALWAYS_REPORT_PROJECTS.includes(import.meta.env.VITE_FB_PROJECT_ID);
  } catch {
    return false;
  }
}

const NAV_FIELDS = ["fetchStart", "workerStart", "requestStart", "responseStart", "domInteractive"];
const USER_ACTIONS = new Set(["tap", "pull"]);

let takenRecord; // undefined = not read yet in this page
let reported = false;

/**
 * Read the record once per page and delete it at once, so a later reload in the
 * same tab never reports it twice. Returns `{ from, at, ev }` or null.
 * `store` is a safeStorage area: the hook runs above the root ErrorBoundary on
 * every route, the public widget embed included, so a blocked storage must
 * never throw here (it blanked the embed on 2026-10-22).
 */
export function takeUpdateTrace(store = localStore) {
  if (takenRecord !== undefined) return takenRecord;
  takenRecord = null;
  const raw = store ? store.get(UPDATE_TRACE_KEY) : null;
  if (raw === null) return takenRecord;
  store.remove(UPDATE_TRACE_KEY);
  try {
    const rec = JSON.parse(raw);
    if (rec && typeof rec === "object" && rec.v === 1 && typeof rec.at === "number" && Number.isFinite(rec.at)
        && typeof rec.from === "string" && Array.isArray(rec.ev)) {
      takenRecord = { from: rec.from, at: rec.at, ev: rec.ev.filter((e) => Array.isArray(e) && typeof e[0] === "number" && typeof e[1] === "string") };
    }
  } catch {
    // not JSON: nothing to report
  }
  return takenRecord;
}

/**
 * This document's navigation timing, whole ms relative to its time origin, and
 * `origin` — when this navigation began, on the record's clock (ms since `at`).
 */
export function readNavigationTiming(recordAt, perf = typeof performance !== "undefined" ? performance : null) {
  let nav = null;
  let origin = null;
  try {
    const entry = perf && typeof perf.getEntriesByType === "function" ? perf.getEntriesByType("navigation")[0] : null;
    if (entry) {
      nav = {};
      for (const f of NAV_FIELDS) nav[f] = typeof entry[f] === "number" ? Math.round(entry[f]) : null;
    }
    if (perf && typeof perf.timeOrigin === "number" && typeof recordAt === "number") origin = Math.round(perf.timeOrigin - recordAt);
  } catch {
    // no timing: the decision falls back to what it has
  }
  return { nav, origin };
}

/**
 * The pure decision. Returns null (send nothing) or `{ message, info }`.
 *   - nothing for an expired record, or one without a tap / pull (an ignored
 *     offer is not a slow update);
 *   - `wait` = origin + nav.responseStart − the first tap / pull (without a
 *     responseStart: origin − first action);
 *   - reported when wait > UPDATE_TRACE_SLOW_MS, or when the page is still on
 *     the record's build (the update did not change the build), or always
 *     with `always` (the test project).
 */
export function updateTraceDecision(record, { now = Date.now(), buildId = BUILD_ID, origin = null, nav = null, env = {}, always = false } = {}) {
  if (!record || !Array.isArray(record.ev)) return null;
  if (!Number.isFinite(record.at) || now - record.at > UPDATE_TRACE_MAX_AGE_MS) return null;
  const actions = record.ev.filter((e) => USER_ACTIONS.has(e[1])).map((e) => e[0]);
  if (!actions.length) return null;
  const firstAction = Math.min(...actions);
  let wait = null;
  if (Number.isFinite(origin)) {
    const response = nav && Number.isFinite(nav.responseStart) ? nav.responseStart : 0;
    wait = origin + response - firstAction;
  }
  const same = !!buildId && record.from === buildId;
  const slow = Number.isFinite(wait) && wait > UPDATE_TRACE_SLOW_MS;
  if (!same && !slow && !always) return null;
  const secs = Number.isFinite(wait) ? `${Math.round(wait / 1000)}s` : "?s";
  const message = same
    ? "update-trace: same build after update"
    : slow ? `update-trace: slow update (${secs})` : `update-trace: update (${secs})`;
  return {
    message,
    info: {
      from: record.from,
      to: buildId || null,
      wait,
      origin,
      nav,
      // objects, not the record's [t, kind, data] triples: Firestore stores no
      // array directly inside an array, and refuses the whole row
      ev: record.ev.map(([t, k, d]) => ({ t, k, d: d ?? null })),
      online: env.online ?? null,
      standalone: env.standalone ?? null,
      conn: env.conn ?? null,
    },
  };
}

function readEnv() {
  const env = { online: null, standalone: null, conn: null };
  try { env.online = navigator.onLine; } catch { /* unknown */ }
  try {
    env.standalone = !!(window.matchMedia && window.matchMedia("(display-mode: standalone)").matches) || navigator.standalone === true;
  } catch { /* unknown */ }
  try { env.conn = (navigator.connection && navigator.connection.effectiveType) || null; } catch { /* unknown */ }
  return env;
}

/** Send the row at most once per page. Returns whether it was sent. */
export function reportUpdateTrace({ log = logCrashError, now = Date.now(), perf, always = alwaysReportHere() } = {}) {
  if (reported) return false;
  const record = takeUpdateTrace();
  if (!record) return false;
  const { nav, origin } = readNavigationTiming(record.at, perf);
  const decision = updateTraceDecision(record, { now, origin, nav, env: readEnv(), always });
  if (!decision) return false;
  reported = true;
  log(decision.message, { source: "update-trace", info: decision.info });
  return true;
}

/** Test seam: forget the per-page record / report state. */
export function __resetUpdateTraceReportForTests() {
  takenRecord = undefined;
  reported = false;
}

/**
 * Mounted once, at the root of `App`. Takes (and deletes) the record at mount —
 * before this page can offer an update of its own and start a new record —
 * then reports after REPORT_DELAY_MS, once the boot has held.
 */
export function useUpdateTraceReport() {
  useEffect(() => {
    takeUpdateTrace();
    // No build id = the dev server: no service worker, no trace.
    if (!BUILD_ID) return undefined;
    const timer = setTimeout(() => { reportUpdateTrace(); }, REPORT_DELAY_MS);
    return () => clearTimeout(timer);
  }, []);
}
